import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect } from "react";
import { Box, useTokens } from "@gemini/core";
import { ArrowLeftIcon, MagnifyingGlassIcon, ArrowRightIcon, } from "@gemini/icons";
import { Button, TextButton } from "@gemini/ui";
import { InfoEmailSentSpot } from "@gemini/illustrations";
import { useAuthContext } from "@/contexts/auth";
import { useClassifiedId, useHardFacts, useTracking, } from "@/contexts/classified";
import { useContactCardSection, useContactSections } from "@/contexts/contact";
import { useI18n } from "@/shared/hooks";
import { useBackToSearch, useTrackingView } from "@/hooks";
import { saveContactToStorage } from "@/utils/saveContactAndSeenToStorage/saveContactAndSeenToStorage";
import { LoginNudge } from "./LoginNudge";
import { SimilarListings } from "./SimilarListings";
import { confirmPageTestId } from "./contacting/constants";
export const ConfirmPage = () => {
    var _a;
    const { t } = useI18n();
    const tokens = useTokens();
    const { authTrackingData } = useAuthContext();
    const { title: agencyTitle } = useContactCardSection();
    const { formatted: formattedPrice } = ((_a = useHardFacts()) === null || _a === void 0 ? void 0 : _a.price) || {};
    const { av_zip_code, av_city } = useTracking();
    const classifiedId = useClassifiedId();
    const isFinanceOfferEnabled = useContactSections().confirmationPage.isFinanceOfferButtonEnabled;
    const isUserLoggedIn = (authTrackingData === null || authTrackingData === void 0 ? void 0 : authTrackingData.status) === "logged";
    const headerHeight = tokens["sizing.64"]; // see https://github.com/axel-springer-kugawana/aviv_whitelabel_header_footer/blob/main/white-label/src/components/header/header.tsx
    useEffect(() => {
        saveContactToStorage(classifiedId);
    }, [classifiedId]);
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);
    const handleBackButton = () => {
        const url = new URL(window.location.href);
        window.location.href = `${url.origin}${url.pathname}`;
    };
    const handleFinanceOfferButtonPress = () => {
        const url = new URL("/immobilienfinanzierung-anfragen/", window.location.origin);
        url.searchParams.set("classifiedId", classifiedId);
        url.searchParams.set("price", formattedPrice);
        url.searchParams.set("zip", av_zip_code);
        url.searchParams.set("city", av_city);
        url.searchParams.set("originvariant", "WL_CONTACT");
        window.location.href = url.href;
    };
    const { handleBackToSearch } = useBackToSearch();
    useTrackingView();
    const agency = t("confirm_page.body", {
        replace: { agencyTitle },
    });
    return (_jsxs(Box, { testId: confirmPageTestId, minHeight: `calc(100vh - ${headerHeight} - 500px)`, maxWidth: "1280px", width: "100%", paddingHorizontal: {
            "breakpoint.base": "spacing.none",
            "breakpoint.lg": "spacing.80",
        }, paddingBottom: {
            "breakpoint.base": "spacing.none",
            "breakpoint.lg": "spacing.24",
        }, marginHorizontal: "auto", display: "flex", flexDirection: "column", gap: "spacing.32", children: [_jsxs(Box, { width: "100%", paddingTop: "spacing.24", paddingHorizontal: {
                    "breakpoint.base": "spacing.16",
                    "breakpoint.lg": "spacing.none",
                }, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: "spacing.24", children: [_jsx(Box, { width: "100%", children: _jsx(TextButton, { icon: ArrowLeftIcon, iconPosition: "start", onPress: handleBackButton, children: t("confirm_page.back_to_listing.cta.label") }) }), _jsxs(Box, { maxWidth: {
                            "breakpoint.base": "280px",
                            "breakpoint.lg": "738px",
                        }, display: "flex", flexDirection: {
                            "breakpoint.base": "column",
                            "breakpoint.lg": "row",
                        }, alignItems: {
                            "breakpoint.base": "center",
                            "breakpoint.lg": "flex-start",
                        }, gap: {
                            "breakpoint.base": "spacing.12",
                            "breakpoint.lg": "spacing.48",
                        }, children: [_jsx(Box, { children: _jsx(InfoEmailSentSpot, { size: "96", "aria-label": "Confirm email info" }) }), _jsxs(Box, { display: "flex", flexDirection: "column", alignItems: {
                                    "breakpoint.base": "center",
                                    "breakpoint.lg": "start",
                                }, gap: "spacing.8", children: [_jsx(Box, { typography: {
                                            "breakpoint.base": "typography.headline.24.bold",
                                            "breakpoint.lg": "typography.headline.32.bold",
                                        }, textAlign: {
                                            "breakpoint.base": "center",
                                            "breakpoint.lg": "left",
                                        }, children: t("confirm_page.headline") }), _jsx(Box, { typography: {
                                            "breakpoint.base": "typography.body.16.regular",
                                            "breakpoint.lg": "typography.headline.20.regular",
                                        }, textAlign: {
                                            "breakpoint.base": "center",
                                            "breakpoint.lg": "left",
                                        }, dangerouslySetInnerHTML: { __html: agency } }), _jsxs(Box, { maxWidth: {
                                            "breakpoint.base": "343px",
                                            "breakpoint.lg": "738px",
                                        }, minWidth: {
                                            "breakpoint.base": "343px",
                                            "breakpoint.lg": "unset",
                                        }, display: "flex", flexDirection: {
                                            "breakpoint.base": "column",
                                            "breakpoint.lg": "row",
                                        }, alignItems: "center", justifyContent: "center", marginTop: "spacing.16", gap: "spacing.12", children: [_jsx(Button, { icon: MagnifyingGlassIcon, iconPosition: "start", onPress: handleBackToSearch, isFullWidth: {
                                                    "breakpoint.base": true,
                                                    "breakpoint.lg": false,
                                                }, children: t("confirm_page.back_to_search.cta.label") }), isFinanceOfferEnabled && (_jsx(Button, { variant: "secondary", icon: ArrowRightIcon, iconPosition: "end", onPress: handleFinanceOfferButtonPress, isFullWidth: {
                                                    "breakpoint.base": true,
                                                    "breakpoint.lg": false,
                                                }, children: t("classified_card.partner_links.financing.empty_price") }))] })] })] })] }), !isUserLoggedIn && _jsx(LoginNudge, {}), _jsx(Box, { width: "100%", minHeight: "520px", paddingTop: {
                    "breakpoint.base": "spacing.none",
                    "breakpoint.lg": "spacing.24",
                }, paddingLeft: {
                    "breakpoint.base": "spacing.24",
                }, borderRadius: {
                    "breakpoint.base": "unset",
                    "breakpoint.lg": "radius.16",
                }, backgroundColor: {
                    "breakpoint.base": "unset",
                    "breakpoint.lg": "color.background.light",
                }, children: _jsx(SimilarListings, {}) })] }));
};
